import { Fragment, useEffect, useState } from 'react';
import { Checkbox, Spinner } from '@skupreme/shared/ui';
import { MuteText, DangerText } from '@elements/Common';
import {
  CheckboxFormField,
  DropdownFormField,
  ControlledTextFormField,
  MaskedNumberFormField,
  MuteLabel,
} from '@elements/FormField';
import { CommonUtility } from '@utility/common';
import {
  BoundingBox,
  Info,
  Upload,
  Warning,
  WarningIcon,
  X,
} from '@phosphor-icons/react';
import { DarkPopup } from '@elements/Popup';
import {
  CatalogHelpText,
  HAZMAT_TEXT_1,
  HAZMAT_TEXT_2,
  HAZMAT_TEXT_3,
} from '@utility/constants/strings';
import { useWatch } from 'react-hook-form';
import {
  AccordionBodyContainer,
  BoldText,
  Row,
  SummaryTitleRow,
} from './styles';
import { RequestedShipping } from '../RequestedShipping';
import { InvalidAddress } from './InvalidAddress';
import { ProductRow } from './ProductRow';
import {
  dropdownValueToFulfillmentProviderSelection,
  fulfillmentProviderSelectionToDropdownValue,
  ProviderDropdown,
  useProviderOptions,
} from './useProviderOptions';

export {
  dropdownValueToFulfillmentProviderSelection,
  fulfillmentProviderSelectionToDropdownValue,
};

const RATE_TEXT = 'sk:text-xl sk:leading-[23px]';

/* Two fields side by side, as Semantic's equal-width group laid them out. */
const FIELD_PAIR = 'sk:grid sk:grid-cols-2 sk:gap-x-3';

function UnitSuffix({ children }: { children: string }) {
  return (
    <span className="sk:pointer-events-none sk:absolute sk:inset-y-0 sk:right-3 sk:flex sk:items-center sk:text-xs sk:text-fg-tertiary">
      {children}
    </span>
  );
}

export function ShippingCreate(props) {
  const {
    addressValidationClick = () => {},
    availableItems = [],
    currentProvider,
    contentTypes = [],
    control,
    customOrigins,
    customOriginLoading,
    isRFCRequired,
    errors,
    enableAutoPrint,
    fpList = [],
    fpLoading = false,
    fulfilledLabels = [],
    fulfillmentProviderChanged = () => {},
    forceGeneratePublicDimension,
    invalidAddress = false,
    addressValidationData = {},
    addressValidationError,
    isNotManualFP = false,
    isUSPS = false,
    isInternational,
    metadataLoading = false,
    packages = [],
    providerChanged = () => {},
    providers = [],
    providerRates = {},
    rate,
    rateError = '',
    rateLoading = false,
    rateNegotiate,
    serviceChanged,
    services = [],
    shippingSpeedData = [],
    requestedShipping = null,
    show,
    showRatesByDefault = false,
    rateCurrency,
    time,
    toggleItems = () => {},
    qtyChanged,
    uploadShippingLabel,
    isSignature,
    setIsSignature,
    currentOrg,
    connectedAccounts,
    setValue,
    ddp,
    fpErrorMessage,
  } = props;
  const [selectedFP, setSelectedFP] = useState(null);
  const [customOriginOptions, setCustomOriginOptions] = useState([]);
  const useCustomOriginName = useWatch({ control, name: 'custom_origin' });

  const isManualFP = !isNotManualFP;

  useEffect(() => {
    let arr = [...customOrigins];
    arr = arr.map(origin => ({
      text: origin.name,
      key: origin.name,
      value: origin.name,
    }));
    // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
    setCustomOriginOptions(arr);
  }, [customOrigins]);

  const addItem = (e, { value }) => {
    // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
    if (!customOriginOptions.find(x => x.value === value)) {
      // @ts-expect-error - TS2345: Argument of type '(options: never[]) => { text: any; key: any; value: any; }[]' ...
      setCustomOriginOptions(options => [
        ...options,
        {
          text: value,
          key: value,
          value,
        },
      ]);
    }
  };

  /**
   * Validates if a number falls within a specified range (minimum: 0, maximum: default 999).
   * @param {number} number - The number to be validated.
   * @param {number} [max=999] - The maximum allowed value (default is 999).
   * @returns {boolean} - Returns true if the number is within the specified range, otherwise returns false.
   */
  const minMaxValidation = (number, max = 999) => !(number > max || number < 0);

  const { providerOptions, providerFetchRateErrorSummary } = useProviderOptions(
    { fpList, providerRates, rateCurrency, currentOrg, connectedAccounts },
  );

  useEffect(() => {
    if (
      CommonUtility.isNotNull(selectedFP) &&
      Object.keys(providerRates).every(rate => !providerRates[rate].loading)
    ) {
      setValue('fulfillment_provider_id', selectedFP);
    }
  }, [providerRates]);

  const hazmatPopupContent = (
    <>
      <p>{HAZMAT_TEXT_1}</p>
      <p>
        {HAZMAT_TEXT_2}{' '}
        <a
          href="https://www.uspsdelivers.com/hazmat-shipping-safety/"
          className="sk:ml-2"
        >
          Example
        </a>
      </p>
      <p>
        {HAZMAT_TEXT_3}{' '}
        <a href="https://www.usps.com/ship/shipping-restrictions.htm">
          Learn more
        </a>
      </p>
    </>
  );

  return (
    <AccordionBodyContainer>
      <div className="sk:mb-2 sk:flex sk:items-center sk:justify-between sk:gap-2">
        <RequestedShipping requested={requestedShipping} />
        {invalidAddress && (
          <InvalidAddress
            onClick={addressValidationClick}
            addressValidationData={addressValidationData}
            addressValidationError={addressValidationError}
          />
        )}
      </div>
      <ProviderDropdown
        required
        options={providerOptions}
        loading={fpLoading}
        name="fulfillment_provider_id"
        error={errors.fulfillment_provider_id}
        hint={errors.fulfillment_provider_id?.message}
        sorted={false}
        placeholder="Ship From"
        defaultOpen={showRatesByDefault}
        label={
          <div className="sk:mb-1 sk:flex sk:items-center sk:gap-2">
            <MuteText>Ship From</MuteText>
            <DarkPopup position="top center" trigger={<Info />}>
              In order to purchase a label you must have at least 1 provider
              with inventory.If you wish to bypass this, navigate to the storage
              provider details page, and check “Fulfill when Out of Stock”
            </DarkPopup>
          </div>
        }
        control={control}
        defaultValue=""
        toDropdownValue={fulfillmentProviderSelectionToDropdownValue}
        fromDropdownValue={dropdownValueToFulfillmentProviderSelection}
        dropdownChanged={item => {
          setSelectedFP(() => item.value);
          fulfillmentProviderChanged(item);
        }}
        fieldClass={enableAutoPrint ? 'sk:mb-0! sk:pb-1' : ''}
      />
      {providerFetchRateErrorSummary.count > 0 && (
        <DarkPopup
          position="top left"
          trigger={
            <div className="sk:mt-1 sk:flex sk:cursor-pointer sk:items-center sk:gap-1">
              <Warning className="sk:shrink-0 sk:text-warning" size={16} />
              <MuteText className="sk:font-normal">
                {providerFetchRateErrorSummary.label}
              </MuteText>
            </div>
          }
        >
          <div>
            <MuteText className="sk:mb-2 sk:block">
              {providerFetchRateErrorSummary.label}.
            </MuteText>
            {providerFetchRateErrorSummary.details.map((row, idx) => (
              <div
                key={`${row.name}-${idx}`}
                className={idx > 0 ? 'sk:mt-2' : ''}
              >
                <BoldText>{row.name}</BoldText>
                <MuteText className="sk:mt-1 sk:block">{row.message}</MuteText>
              </div>
            ))}
          </div>
        </DarkPopup>
      )}
      {currentProvider?.address && (
        <MuteText>
          Address: {CommonUtility.addressText(currentProvider?.address)}
        </MuteText>
      )}
      {enableAutoPrint && (
        <div className="sk:mt-3">
          <div className="sk:flex sk:items-center sk:gap-2">
            <CheckboxFormField
              name="custom_origin"
              error={errors.custom_origin}
              hint={errors.custom_origin?.message}
              control={control}
            />
            <MuteLabel>Use Custom Origin</MuteLabel>
          </div>
          {useCustomOriginName && (
            <ProviderDropdown
              required
              options={customOriginOptions}
              loading={customOriginLoading}
              name="origin_name"
              error={errors.origin_name}
              hint={errors.origin_name?.message}
              sorted={false}
              placeholder="Origin Name"
              control={control}
              defaultValue=""
              allowAdditions
              onAddItem={addItem}
            />
          )}
        </div>
      )}
      {fpErrorMessage && (
        <div>
          <DarkPopup
            trigger={
              <div className="sk:mb-2 sk:flex sk:cursor-pointer sk:items-center sk:gap-1">
                <DangerText>Error</DangerText>
                <WarningIcon className="sk:text-danger" />
              </div>
            }
          >
            {fpErrorMessage}
          </DarkPopup>
        </div>
      )}

      {fulfilledLabels.length > 0 && (
        <>
          <BoldText>Fulfilled Labels</BoldText>
          {fulfilledLabels.map(item => (
            <Fragment key={item._id}>
              <Row disabled>
                {/* @ts-expect-error - TS2739: Type '{ item: any; }' is missing the following properties fr... */}
                <ProductRow item={item} />
              </Row>
            </Fragment>
          ))}
        </>
      )}

      {availableItems.length > 0 && (
        <div>
          <div className="sk:flex sk:items-center sk:justify-between">
            <div className="sk:flex sk:items-center sk:gap-1">
              <BoldText className="sk:flex sk:items-center sk:gap-2">
                {isNotManualFP ? 'Fulfilling' : 'Create Labels'}
                {fpLoading && <Spinner size="sm" />}
              </BoldText>
              {currentProvider?.packages?.length > 1 && (
                <DarkPopup
                  trigger={
                    <div className="sk:flex sk:items-center sk:gap-1">
                      <BoldText>
                        {currentProvider?.packages?.length} Packages
                      </BoldText>
                      <Info />
                    </div>
                  }
                  position="right center"
                >
                  This order fulfillment will be split into{' '}
                  {currentProvider?.packages?.length} labels.
                </DarkPopup>
              )}
            </div>
            {isNotManualFP && !currentProvider?.isFBA && (
              <div className="sk:flex sk:items-center sk:gap-3">
                <DarkPopup
                  trigger={
                    <BoundingBox
                      className="sk:cursor-pointer"
                      onClick={forceGeneratePublicDimension}
                      size={15}
                      weight="duotone"
                    />
                  }
                >
                  Customize Packaging
                </DarkPopup>
                <DarkPopup
                  trigger={
                    <Upload
                      className="sk:cursor-pointer"
                      onClick={uploadShippingLabel}
                      size={15}
                    />
                  }
                >
                  Attach Label
                </DarkPopup>
              </div>
            )}
          </div>
          {availableItems.map(item => (
            <Fragment key={item._id}>
              <Row disabled={!item.available}>
                <Checkbox
                  aria-label={`Ship ${item.p_sku || item.p_title || 'item'}`}
                  className="sk:mr-2"
                  isSelected={!!item.checked}
                  onChange={isChecked => toggleItems(item, isChecked)}
                  isDisabled={!item.available}
                />
                {/* @ts-expect-error - TS2741: Property 'price' is missing in type '{ item: any; qty: any; canChangeQty: boolea... */}
                <ProductRow
                  isInternational={isInternational}
                  item={item}
                  qty={item.newQty || item.qty - item.fulfilledQty}
                  canChangeQty
                  qtyChanged={qty => qtyChanged(item, qty)}
                />
              </Row>
            </Fragment>
          ))}
        </div>
      )}
      {isManualFP ? (
        <>
          {show.weight && (
            <div className={FIELD_PAIR}>
              <MaskedNumberFormField
                label="Weight"
                name="pounds"
                error={errors.pounds}
                defaultValue=""
                control={control}
                min={0}
                placeholder="lbs"
                inputRight={<UnitSuffix>lbs</UnitSuffix>}
                // @ts-expect-error - TS2322: Type '(event: React.FocusEvent<HTMLInputElement> | null) => void' is not assigna...
                onFocus={CommonUtility.selectInputOnFocus}
                required
              />
              <MaskedNumberFormField
                label={<br />}
                name="ounces"
                error={errors.pounds}
                control={control}
                min={0}
                placeholder="oz"
                defaultValue={0}
                inputRight={<UnitSuffix>oz</UnitSuffix>}
                minMaxValidation={num => minMaxValidation(num, 99)}
                maskOptions={{
                  integerLimit: 2,
                }}
                // @ts-expect-error - TS2322: Type '(event: React.FocusEvent<HTMLInputElement> | null) => void' is not assigna...
                onFocus={CommonUtility.selectInputOnFocus}
              />
            </div>
          )}
          <div className={FIELD_PAIR}>
            <DropdownFormField
              required
              options={providers}
              loading={metadataLoading}
              name="provider"
              error={errors.provider}
              label="Shipping Carrier"
              control={control}
              defaultValue=""
              dropdownChanged={providerChanged}
              sorted={false}
            />
            <DropdownFormField
              required
              options={services}
              loading={metadataLoading}
              name="service"
              error={errors.service}
              label="Service"
              control={control}
              defaultValue=""
              dropdownChanged={serviceChanged}
              sorted={false}
            />
          </div>
          <DropdownFormField
            required
            options={packages}
            loading={metadataLoading}
            name="package"
            error={errors.package}
            label="Package"
            control={control}
            defaultValue=""
            sorted={false}
          />
          {show.dimensions && (
            <div className="sk:flex sk:items-start sk:justify-between sk:gap-2 sk:[&_input]:w-20!">
              <MaskedNumberFormField
                label="Size (in)"
                name="length"
                error={errors.length}
                control={control}
                min={0}
                placeholder="L"
                defaultValue={0}
                maskOptions={{
                  integerLimit: 3,
                }}
                // @ts-expect-error - TS2322: Type '(event: React.FocusEvent<HTMLInputElement> | null) => void' is not assigna...
                onFocus={CommonUtility.selectInputOnFocus}
              />
              <X className="sk:mt-8 sk:shrink-0" size={16} />
              <MaskedNumberFormField
                label={<br />}
                name="width"
                error={errors.width}
                control={control}
                min={0}
                placeholder="W"
                defaultValue={0}
                maskOptions={{
                  integerLimit: 3,
                }}
                // @ts-expect-error - TS2322: Type '(event: React.FocusEvent<HTMLInputElement> | null) => void' is not assigna...
                onFocus={CommonUtility.selectInputOnFocus}
              />
              <X className="sk:mt-8 sk:shrink-0" size={16} />
              <MaskedNumberFormField
                label={<br />}
                name="height"
                error={errors.height}
                control={control}
                min={0}
                placeholder="H"
                defaultValue={0}
                maskOptions={{
                  integerLimit: 3,
                }}
                // @ts-expect-error - TS2322: Type '(event: React.FocusEvent<HTMLInputElement> | null) => void' is not assigna...
                onFocus={CommonUtility.selectInputOnFocus}
              />
            </div>
          )}
          <DropdownFormField
            options={[]}
            name="options.insurance"
            label="Insurance"
            control={control}
            defaultValue=""
          />
          <DropdownFormField
            options={contentTypes}
            name="customsContentType"
            label="Content Type"
            control={control}
            defaultValue=""
            required={isInternational}
            error={errors.customsContentType}
            hint={errors.customsContentType?.message}
          />
          {isRFCRequired && (
            <ControlledTextFormField
              name="options.tax_code"
              control={control}
              placeholder="Enter Mexican Tax ID"
              label="RFC Code"
              fieldClass="text-id-text sk:mt-2"
            />
          )}
          <ControlledTextFormField
            name="contentComments"
            control={control}
            label="Comments"
            fieldClass="shipping-lable-text"
          />
          {isUSPS && (
            <div className="sk:flex sk:items-start sk:gap-1">
              <CheckboxFormField
                name="options.hazmat"
                label=""
                text="Is this Hazmat?"
                control={control}
                defaultValue={false}
              />
              <DarkPopup
                position="bottom center"
                hoverable
                wide
                trigger={
                  <Info height={13} width={13} className="sk:cursor-pointer" />
                }
              >
                {hazmatPopupContent}
              </DarkPopup>
            </div>
          )}
          <CheckboxFormField
            name="packing_list"
            label=""
            text="Packing List"
            control={control}
            defaultValue={false}
          />
          <CheckboxFormField
            name="saveDefault"
            label=""
            text="Save as shipping defaults for this product"
            control={control}
            defaultValue={false}
          />
          {ddp ? (
            <>
              <BoldText>Label Cost</BoldText>
              <Row className="sk:mt-1">
                <MuteText>Duties (DDP)</MuteText>
                <span>
                  {CommonUtility.currencyFormat(ddp?.duties, rateCurrency)}
                </span>
              </Row>
              <Row>
                <MuteText>Taxes</MuteText>
                <span>
                  {CommonUtility.currencyFormat(ddp?.taxes, rateCurrency)}
                </span>
              </Row>
              <Row>
                <MuteText>Fees</MuteText>
                <span>
                  {CommonUtility.currencyFormat(ddp?.fees, rateCurrency)}
                </span>
              </Row>
              <SummaryTitleRow className="sk:mb-3">
                <Row>
                  <MuteText>Label Total</MuteText>
                  <span>
                    {CommonUtility.currencyFormat(ddp?.total, rateCurrency)}
                  </span>
                </Row>
              </SummaryTitleRow>
              <BoldText>Rate</BoldText>
              <Row>
                <MuteText>Shipping</MuteText>
                <span>
                  {rateLoading
                    ? 'Loading'
                    : rateError
                      ? '-'
                      : CommonUtility.currencyFormat(rate, rateCurrency)}
                </span>
              </Row>
              <Row>
                <MuteText>Label Total</MuteText>
                <span>
                  {CommonUtility.currencyFormat(ddp?.total, rateCurrency)}
                </span>
              </Row>
              <SummaryTitleRow className="sk:py-2">
                <Row>
                  <MuteText>Rate Total</MuteText>
                  <BoldText className={RATE_TEXT}>
                    {CommonUtility.currencyFormat(
                      ddp?.total + rate,
                      rateCurrency,
                    )}
                  </BoldText>
                </Row>
              </SummaryTitleRow>
            </>
          ) : (
            <Row>
              <div>
                Rate&nbsp;&nbsp;
                <BoldText className={RATE_TEXT}>
                  {rateLoading
                    ? 'Loading'
                    : rateError
                      ? '-'
                      : CommonUtility.currencyFormat(rate, rateCurrency)}
                </BoldText>
                {rateNegotiate && (
                  <span className="sk:text-[0.7rem] sk:leading-[0.8rem] sk:text-fg-tertiary">
                    (Negotiated rate)
                  </span>
                )}
              </div>
              <div>
                Delivery Time&nbsp;&nbsp;
                <BoldText>{rateLoading ? 'Loading' : time || '-'}</BoldText>
              </div>
            </Row>
          )}
          {rateError && (
            <Row className="sk:my-2">
              <DangerText>{rateError}</DangerText>
            </Row>
          )}
        </>
      ) : (
        <>
          <DropdownFormField
            options={shippingSpeedData}
            name="shipping_speed"
            error={errors.shipping_speed}
            label="Fulfillment Type"
            control={control}
            defaultValue=""
          />
          <div className="sk:flex sk:items-center sk:gap-1">
            <Checkbox
              name="signature"
              isSelected={!!isSignature}
              onChange={setIsSignature}
            >
              <BoldText>Is Signature required?</BoldText>
            </Checkbox>
            <DarkPopup trigger={<Info className="sk:cursor-pointer" />}>
              {CatalogHelpText.shipWithSignature}
            </DarkPopup>
          </div>
          {isRFCRequired && (
            <ControlledTextFormField
              name="options.tax_code"
              control={control}
              placeholder="Enter Mexican Tax ID"
              label="RFC Code"
              fieldClass="text-id-text sk:mt-3"
            />
          )}
        </>
      )}
    </AccordionBodyContainer>
  );
}
